import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { ChevronDown, ChevronRight, MoreHorizontal, Plus } from "lucide-react";
import { EmptyState } from "./EmptyState";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import type { FieldDef, Json, KanbanNode, KanbanPlan, RecordData } from "../core";
import { Cell } from "./Cell";
import { groupKeyOf, readFieldValue, recordId, refLabel } from "./cell_utils";

export interface GroupOption {
    value: Json;
    label: string;
    color?: Json; // palette index from a stage's color field, if any
    fold?: boolean; // stage marked folded-by-default
}

interface Column {
    id: string;
    label: string;
    value: Json | null; // raw group value to write on drop (ref id / enum key / null)
    color?: Json;
    records: RecordData[];
}

// Normalize a group-by value to what we'd persist (reference → id, else scalar).
function groupValueOf(value: Json | undefined): Json | null {
    if (value == null || value === "") return null;
    if (typeof value === "object" && !Array.isArray(value) && "id" in value) {
        return (value as { id?: Json }).id ?? null;
    }
    return value;
}

function buildColumns(records: RecordData[], groupField: string, groupOptions?: GroupOption[]): Column[] {
    if (!groupField) return [{ id: "__all", label: "All", value: null, records }];

    // Preferred: one column per possible value (all stages, even empty), with an
    // "Undefined" column for records that have no group value.
    if (groupOptions && groupOptions.length) {
        const byValue = new Map<string, RecordData[]>();
        const undefined_: RecordData[] = [];
        for (const rec of records) {
            const gv = groupValueOf(rec[groupField]);
            if (gv == null) {
                undefined_.push(rec);
                continue;
            }
            const key = String(gv);
            const bucket = byValue.get(key);
            if (bucket) bucket.push(rec);
            else byValue.set(key, [rec]);
        }
        const cols: Column[] = groupOptions.map((o) => ({
            id: String(o.value),
            label: o.label,
            value: o.value,
            color: o.color,
            records: byValue.get(String(o.value)) ?? [],
        }));
        if (undefined_.length) cols.push({ id: "__undef", label: "Undefined", value: null, records: undefined_ });
        return cols;
    }

    // Fallback: derive columns from the data (only non-empty groups).
    const cols = new Map<string, Column>();
    for (const rec of records) {
        const raw = rec[groupField];
        const label = groupKeyOf(raw);
        const existing = cols.get(label);
        if (existing) existing.records.push(rec);
        else cols.set(label, { id: label, label, value: groupValueOf(raw), records: [rec] });
    }
    return [...cols.values()];
}

function renderNode(node: KanbanNode, record: RecordData, fieldMap: Map<string, FieldDef>, wrap: boolean): ReactNode {
    switch (node.type) {
        case "KanbanCard":
            return node.children?.map((c, i) => <Fragment key={i}>{renderNode(c, record, fieldMap, false)}</Fragment>);
        case "KanbanTitle":
            return <div className="kanban__card-title">{renderChildren(node, record, fieldMap, false)}</div>;
        case "KanbanSubtitle":
            return <div className="kanban__card-sub">{renderChildren(node, record, fieldMap, false)}</div>;
        case "KanbanRow":
        case "KanbanFooter":
        case "KanbanStack":
            return <div className="kanban__card-meta">{renderChildren(node, record, fieldMap, true)}</div>;
        case "KanbanRibbon": {
            const field = node.attrs?.field ? String(node.attrs.field) : null;
            if (!field) return null;
            const ribbonDef = fieldMap.get(field);
            return (
                <span className="kanban__card-badge">
                    <Cell value={readFieldValue(record, ribbonDef, field)} fieldDef={ribbonDef} />
                </span>
            );
        }
        case "field": {
            if (node.invisible || !node.name) return null;
            // Custom-property fields store their value at record[bag][key].
            const fieldDef = fieldMap.get(node.name);
            const cell = (
                <Cell
                    value={readFieldValue(record, fieldDef, node.name)}
                    widget={node.widget}
                    fieldDef={fieldDef}
                />
            );
            return wrap ? <span className="kanban__card-badge">{cell}</span> : cell;
        }
        default:
            return null; // button / separator — later pass
    }
}

function renderChildren(node: KanbanNode, record: RecordData, fieldMap: Map<string, FieldDef>, wrap: boolean): ReactNode {
    return node.children?.map((c, i) => <Fragment key={i}>{renderNode(c, record, fieldMap, wrap)}</Fragment>);
}

function KanbanCard({
    rec,
    plan,
    fieldMap,
    onOpenRecord,
    draggable,
}: {
    rec: RecordData;
    plan: KanbanPlan;
    fieldMap: Map<string, FieldDef>;
    onOpenRecord: (id: string) => void;
    draggable: boolean;
}) {
    const id = recordId(rec);
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled: !draggable });
    return (
        <div
            ref={setNodeRef}
            className={`kanban__card ${isDragging ? "is-dragging" : ""}`}
            onClick={() => onOpenRecord(id)}
            data-viewid="ede-kanban-card"
            data-record-id={id}
            {...attributes}
            {...listeners}
        >
            {plan.cardTemplate ? (
                renderNode(plan.cardTemplate, rec, fieldMap, false)
            ) : (
                <div className="kanban__card-title">{refLabel(rec)}</div>
            )}
        </div>
    );
}

function KanbanColumn({
    col,
    plan,
    fieldMap,
    grouped,
    collapsed,
    quickCreateField,
    onToggle,
    onOpenRecord,
    onCreate,
    onQuickCreate,
}: {
    col: Column;
    plan: KanbanPlan;
    fieldMap: Map<string, FieldDef>;
    grouped: boolean;
    collapsed: boolean;
    quickCreateField: string;
    onToggle: (id: string) => void;
    onOpenRecord: (id: string) => void;
    onCreate: (groupValue: Json | null) => void;
    onQuickCreate?: (groupValue: Json | null, values: Record<string, Json>) => Promise<void> | void;
}) {
    const { setNodeRef, isOver } = useDroppable({ id: col.id, disabled: !grouped });
    const [creating, setCreating] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const beginCreate = () => {
        if (onQuickCreate) {
            setCreating(true);
            setDraftName("");
        } else {
            // No inline handler wired — fall back to opening the full form.
            onCreate(col.value);
        }
    };
    const cancelCreate = () => {
        setCreating(false);
        setDraftName("");
    };
    const submitCreate = async () => {
        if (!onQuickCreate) return;
        const trimmed = draftName.trim();
        if (!trimmed) return;
        setSubmitting(true);
        try {
            await onQuickCreate(col.value, { [quickCreateField]: trimmed });
            setDraftName("");
            // Keep `creating` true so the user can add a second card without
            // re-clicking +. ESC / blur dismisses.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            ref={setNodeRef}
            className={`kanban__col ${collapsed ? "is-collapsed" : ""} ${isOver ? "is-drop-target" : ""}`}
            data-viewid="ede-kanban-column"
            data-stage={col.value == null ? "" : String(col.value)}
        >
            <button type="button" className="kanban__col-head" onClick={() => onToggle(col.id)}>
                {collapsed ? <ChevronRight size={14} className="kanban__col-chev" /> : <ChevronDown size={14} className="kanban__col-chev" />}
                {col.color != null ? <span className="kanban__col-dot" data-color={String(col.color)} /> : null}
                <span className="kanban__col-title">{col.label}</span>
                <span className="kanban__col-count">{col.records.length}</span>
                {!collapsed ? (
                    <span className="kanban__col-actions">
                        {plan.quickCreate ? (
                            <span
                                className="kanban__col-menu"
                                role="button"
                                aria-label="New record"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    beginCreate();
                                }}
                            >
                                <Plus size={14} />
                            </span>
                        ) : null}
                        <span
                            className="kanban__col-menu"
                            role="button"
                            aria-label="Column actions"
                            title="Column actions"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MoreHorizontal size={14} />
                        </span>
                    </span>
                ) : null}
            </button>
            {!collapsed ? (
                <div className="kanban__cards">
                    {creating ? (
                        <div className="kanban__quick-create" onClick={(e) => e.stopPropagation()}>
                            <input
                                className="kanban__quick-create-input"
                                autoFocus
                                placeholder={`New ${col.label.toLowerCase()}…`}
                                value={draftName}
                                onChange={(e) => setDraftName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        void submitCreate();
                                    } else if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelCreate();
                                    }
                                }}
                                onBlur={() => {
                                    // Trailing blur cancels only when input is empty —
                                    // otherwise lets the user keep editing if focus
                                    // briefly moves elsewhere (e.g. clicking the
                                    // submit hint button later).
                                    if (!draftName.trim() && !submitting) cancelCreate();
                                }}
                                disabled={submitting}
                            />
                            <div className="kanban__quick-create-hint">
                                Enter to add · Esc to cancel
                            </div>
                        </div>
                    ) : null}
                    {col.records.map((rec) => (
                        <KanbanCard
                            key={recordId(rec)}
                            rec={rec}
                            plan={plan}
                            fieldMap={fieldMap}
                            onOpenRecord={onOpenRecord}
                            draggable={grouped}
                        />
                    ))}
                    {col.records.length === 0 && !creating ? <EmptyState size="sm" title="No records" /> : null}
                </div>
            ) : null}
        </div>
    );
}

interface KanbanViewProps {
    plan: KanbanPlan;
    // Active grouping axis: user's groupBy[0] (from SearchPanel) or the DSL's
    // default_group_by, resolved by ActionManager. null = flat card grid.
    groupAxis: string | null;
    fieldDefs: FieldDef[];
    records: RecordData[];
    isLoading: boolean;
    groupOptions?: GroupOption[];
    collapsed: Set<string>;
    onToggleColumn: (id: string) => void;
    onOpenRecord: (id: string) => void;
    // Fallback path: opens the full form when no inline quick-create handler
    // is wired or when a column carries no group value to pre-fill.
    onCreate: (groupValue: Json | null) => void;
    // Optional inline quick-create handler. When provided, the column-foot `+`
    // button opens an inline mini-form that pre-fills the `quickCreateField`
    // with the typed name and the active groupby axis with the column's
    // group value, then dispatches a record create.
    onQuickCreate?: (groupValue: Json | null, values: Record<string, Json>) => Promise<void> | void;
    // `mode` is resolved here from `plan.onDrag` so the consumer (ActionManager)
    // knows whether to fire a workflow transition (audit + guards + hooks) or
    // a plain field write. Wire format = `field` | `workflow`.
    onDropCard: (recordId: string, groupValue: Json | null, mode: "field" | "workflow") => void;
}

export function KanbanView({
    plan,
    groupAxis,
    fieldDefs,
    records,
    isLoading,
    groupOptions,
    collapsed,
    onToggleColumn,
    onOpenRecord,
    onCreate,
    onQuickCreate,
    onDropCard,
}: KanbanViewProps) {
    const fieldMap = new Map(fieldDefs.map((f) => [f.name, f]));
    // The field a quick-create input writes into. Defaults to `name` — the
    // most common record-display field. Long-term this should come from the
    // model's `__ede_record_name_field__` plumbed through KanbanPlan.
    const quickCreateField = "name";
    const grouped = Boolean(groupAxis);
    const columns = grouped && groupAxis ? buildColumns(records, groupAxis, groupOptions) : [];
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    // Tracks the currently-dragging card id so <DragOverlay> can render a
    // visible ghost that follows the cursor (useDraggable alone doesn't
    // produce a moving ghost — without an overlay the only feedback is the
    // is-dragging style on the original card).
    const [activeId, setActiveId] = useState<string | null>(null);
    const activeRecord = activeId ? records.find((r) => recordId(r) === activeId) : null;

    // Wholesale empty surface — only when ungrouped OR no columns to show.
    // Grouped + has column options → render the board so the user still sees
    // the stages; per-column empty state inside KanbanColumn handles the rest.
    if (records.length === 0 && (!grouped || (groupOptions ?? []).length === 0)) {
        return (
            <div className="kanban" data-viewid="ede-kanban">
                {isLoading ? (
                    <div className="list-view__state">Loading…</div>
                ) : (
                    <EmptyState
                        title="No records to show"
                        hint="Adjust your filters or create a new record to get started."
                    />
                )}
            </div>
        );
    }

    const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
    const onDragEnd = (e: DragEndEvent) => {
        setActiveId(null);
        if (!grouped || !e.over) return;
        const cardId = String(e.active.id);
        const targetId = String(e.over.id);
        const source = columns.find((c) => c.records.some((r) => recordId(r) === cardId));
        if (source && source.id === targetId) return;
        const target = columns.find((c) => c.id === targetId);
        if (!target) return;
        // Resolve the drop mode from plan.onDrag. "auto" is conservative: until
        // proper field-type metadata flows through KanbanPlan (Phase 4.5 work),
        // auto resolves to "field" — the safe default that just writes the
        // groupby axis. Explicit "workflow" routes through the transition API.
        const mode: "field" | "workflow" = plan.onDrag === "workflow" ? "workflow" : "field";
        onDropCard(cardId, target.value, mode);
    };
    const onDragCancel = () => setActiveId(null);

    // No group-by → a plain card grid, no columns / grouping chrome.
    if (!grouped) {
        return (
            <div className="kanban" data-viewid="ede-kanban">
                <DndContext sensors={sensors} onDragEnd={onDragEnd}>
                    <div className="kanban__flat">
                        {records.map((rec) => (
                            <KanbanCard
                                key={recordId(rec)}
                                rec={rec}
                                plan={plan}
                                fieldMap={fieldMap}
                                onOpenRecord={onOpenRecord}
                                draggable={false}
                            />
                        ))}
                    </div>
                </DndContext>
            </div>
        );
    }

    return (
        <div className="kanban" data-viewid="ede-kanban">
            <DndContext
                sensors={sensors}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragCancel={onDragCancel}
            >
                <div className="kanban__board">
                    {columns.map((col) => (
                        <KanbanColumn
                            key={col.id}
                            col={col}
                            plan={plan}
                            fieldMap={fieldMap}
                            grouped={grouped}
                            collapsed={collapsed.has(col.id)}
                            quickCreateField={quickCreateField}
                            onToggle={onToggleColumn}
                            onOpenRecord={onOpenRecord}
                            onCreate={onCreate}
                            onQuickCreate={onQuickCreate}
                        />
                    ))}
                </div>
                <DragOverlay dropAnimation={null}>
                    {activeRecord ? (
                        <div className="kanban__card kanban__card--ghost">
                            {plan.cardTemplate
                                ? renderNode(plan.cardTemplate, activeRecord, fieldMap, false)
                                : <div className="kanban__card-title">{refLabel(activeRecord)}</div>}
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </div>
    );
}
